import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Blocks, Trash2, Layers, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from './axiosConfig';
import ListPageShell from './list/ListPageShell';
import { resolveIcon } from './iconResolver';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import CustomModelRecycleBin from './CustomModelRecycleBin';

// 🎯 شاشة إدارة التطبيقات المخصصة - لستة بكل الموديلز الكستم (منشورة أو
// لسه مسودة)، بتفتح الـ Studio لإنشاء/تعديل أي واحد فيهم
const CustomAppList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();

  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRecycleBin, setShowRecycleBin] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/custom-models', { params: { published_only: false } });
      setModels(res.data || []);
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load custom applications'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleTogglePublish = async (model) => {
    try {
      const res = await api.put(`/custom-models/${model.slug}`, { is_published: !model.is_published });
      setModels((prev) => prev.map((m) => (m.slug === model.slug ? res.data : m)));
    } catch (err) {
      toast.error(t('customApps.publishFailed', 'Failed to update'));
    }
  };

  // 🎯 حذف موديل كستم كامل بيمسح معاه كل سجلاته والسكربتات بتاعته من غير
  // رجعة (main.py: delete_custom_model بيمسح CustomRecord وCustomActionScript
  // يدويًا قبل ما يمسح الموديل نفسه) - ده مش زي حذف سجل واحد، فلازم تأكيد
  // واضح يقول ده صراحة قبل ما يحصل، مش بس زرار سلة مهملات عادي
  const handleDelete = async (model) => {
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t(
        'customApps.deleteAppConfirm',
        'This will move the "{{name}}" app to the recycle bin. It can be restored later from there.',
        { name: model.label }
      ),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yesDelete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      reverseButtons: isRTL,
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/custom-models/${model.slug}`);
      toast.success(t('common.deleted', 'Deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.deleteFailed', 'Delete failed'));
    }
  };

  const filtered = models.filter((m) => m.label.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <ListPageShell
      icon={<Blocks size={22} className="text-primary-dark" />}
      title={t('customApps.title', 'Custom Applications')}
      loading={loading}
      onRefresh={load}
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('common.search', 'Search')}
      newButtonLabel={t('customApps.newApp', 'New Application')}
      onNew={() => navigate('/admin/custom-apps/new/studio')}
      isRTL={isRTL}
      toolbarExtra={
        <>
          <button
            onClick={() => navigate('/admin/custom-apps/extend')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-ink-secondary text-[10px] font-bold uppercase hover:bg-primary-soft transition-all"
          >
            <Layers size={13} /> {t('customApps.extendScreens.title', 'Extend Existing Screens')}
          </button>
          <button
            onClick={() => setShowRecycleBin(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-ink-secondary text-[10px] font-bold uppercase hover:bg-primary-soft transition-all"
          >
            <RotateCcw size={13} /> {t('customApps.recycleBin', 'Recycle Bin')}
          </button>
        </>
      }
    >
      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-20">
          <tr>
            <th className="p-3 border-r border-white/10">{t('customApps.table.app', 'Application')}</th>
            <th className="p-3 border-r border-white/10">{t('customApps.table.slug', 'Slug')}</th>
            <th className="p-3 border-r border-white/10">{t('customApps.table.status', 'Status')}</th>
            <th className="p-3 w-24" />
          </tr>
        </thead>
        <tbody>
          {filtered.map((m) => {
            const Icon = resolveIcon(m.icon_name);
            return (
              <tr
                key={m.slug}
                className="border-b hover:bg-primary-soft cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/custom-apps/${m.slug}/studio`)}
              >
                <td className="p-3 font-bold text-gray-700">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-primary" />
                    {m.label}
                  </div>
                </td>
                <td className="p-3 font-bold text-gray-400 text-xs">{m.slug}</td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleTogglePublish(m)}
                    className={`text-[9px] px-2 py-1 rounded-full uppercase font-bold ${m.is_published ? 'bg-status-success-bg text-status-success' : 'bg-status-warning-bg text-status-warning'}`}
                  >
                    {m.is_published ? t('customApps.published', 'Published') : t('customApps.draft', 'Draft')}
                  </button>
                </td>
                <td className="p-3 text-center">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(m); }}
                    className="text-gray-300 hover:text-status-danger transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={4} className="p-8 text-center text-gray-400 font-bold">
                {t('common.noRecords', 'No records found')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <CustomModelRecycleBin
        isOpen={showRecycleBin}
        onClose={() => setShowRecycleBin(false)}
        onRestored={load}
      />
    </ListPageShell>
  );
};

export default CustomAppList;
